import { useState, useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import instance from '../config/axios';
import { Product } from '../interface/interface';
import { useQuery } from '@tanstack/react-query';

export function useProductDetails(
  productId: string | undefined,
  isGuest: boolean,
) {
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const fetchPrescriptionDetails = async (id: string, token: string) => {
    const response = await instance.get(`api/v1/prescription/product/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  };

  const { data: prescription } = useQuery({
    queryKey: ['prescription-details', productId, user?.access_token],
    queryFn: () =>
      fetchPrescriptionDetails(
        productId as string,
        user!.access_token as string,
      ),
    enabled:
      !isGuest &&
      !!productId &&
      !!user?.access_token &&
      !!product?.prescription_required,
  });

  const fetchSimilarProducts = async (categoryId: number) => {
    const endpoint = isGuest
      ? `guest/similar-products/${categoryId}`
      : `api/v1/similar/product/${categoryId}`;
    const headers = isGuest
      ? {}
      : { Authorization: `Bearer ${user?.access_token}` };
    const response = await instance.get(endpoint, { headers });
    return response.data;
  };

  const { data: similarProduct } = useQuery({
    queryKey: ['similar-products', product?.productCategory?.id, isGuest],
    queryFn: () => fetchSimilarProducts(product!.productCategory.id),
    enabled: product?.productCategory?.id != null,
  });

  useEffect(() => {
    if (!isGuest && !user?.access_token) return;

    const load = async () => {
      setLoading(true);
      try {
        const endpoint = isGuest
          ? `/guest/product/${productId}`
          : `api/v1/product/${productId}`;
        const headers = isGuest
          ? {}
          : { Authorization: `Bearer ${user?.access_token}` };
        const response = await instance.get(endpoint, { headers });
        setProduct(response.data);

        if (response.data?.prescription_required) {
          await fetchPrescriptionDetails(productId!, user?.access_token!);
        } else {
          await fetchSimilarProducts(response.data.productCategory.id);
        }
      } catch (err) {
        console.error(
          `Error at get ${isGuest ? 'Guest ' : ''}Product Details`,
          err,
        );
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [isGuest, user?.access_token, productId]);

  return { product, prescription, similarProduct, loading };
}
